import type { GateType, LogicNode } from '../../../types/EMRALD_Model';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { MainDetailsForm } from '../MainDetailsForm';
import { useLogicNodeFormContext } from './LogicNodeFormContext';
import { StateValuesTable } from './StateValuesTable';

interface LogicNodeFormProps {
  logicNodeData?: LogicNode;
  parentNodeName?: string;
  nodeType?: 'gate' | 'comp';
  gateType?: GateType;
  component?: string;
  editing?: boolean;
  setAsRoot?: boolean;
  fromSidebar?: boolean;
}

export const LogicNodeForm: React.FC<LogicNodeFormProps> = ({
  logicNodeData,
  gateType,
  nodeType,
  component,
  editing,
  parentNodeName,
  fromSidebar,
}) => {
  const {
    name,
    desc,
    gateTypeValue,
    leafNodeType,
    compDiagram,
    componentDiagrams,
    defaultValues,
    currentNode,
    gateTypeOptions,
    isRoot,
    hasError,
    reqPropsFilled,
    setDesc,
    setLeafNodeType,
    setCompDiagram,
    setNewCompChild,
    setDefaultValues,
    setCurrentNodeStateValues,
    setGateTypeValue,
    setIsRoot,
    handleNameChange,
    handleSave,
    handleClose,
    availableAsTopOrSubtree,
    checkForDuplicateNames,
    initializeForm,
  } = useLogicNodeFormContext();

  useEffect(() => {
    initializeForm(
      logicNodeData,
      editing,
      component,
      parentNodeName,
      nodeType,
      gateType,
      fromSidebar ? true : undefined,
    );
  }, []);

  return (
    <Box mx={3} pb={3}>
      <Typography variant="h5" my={3}>
        {logicNodeData ? 'Edit' : 'Create New'}{' '}
        {leafNodeType === 'comp' ? 'Component' : 'Gate'}
      </Typography>
      <form>
        {leafNodeType ? (
          <>
            {editing ? (
              <></>
            ) : (
              <>
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 120, width: '100%', mb: 3 }}
                >
                  <InputLabel id="node-type-select-label">Node Type</InputLabel>
                  <Select
                    labelId="node-type-select-label"
                    id="node-type-select"
                    value={leafNodeType}
                    onChange={(event: SelectChangeEvent) => {
                      setLeafNodeType(event.target.value);
                    }}
                    label="Node Type"
                  >
                    <MenuItem key="gate" value="gate">
                      Gate
                    </MenuItem>
                    <MenuItem key="comp" value="comp">
                      Component
                    </MenuItem>
                  </Select>
                </FormControl>
                <Divider sx={{ mb: 3 }} />
              </>
            )}
          </>
        ) : (
          <></>
        )}

        {leafNodeType === 'comp' ? (
          <>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 120, width: '100%', mb: 3 }}
            >
              <InputLabel id="comp-select-label">Component Diagrams</InputLabel>
              <Select
                labelId="node-type-select-label"
                id="node-type-select"
                value={compDiagram}
                onChange={(event: SelectChangeEvent) => {
                  setCompDiagram(event.target.value);
                  if (!component) {
                    setNewCompChild({
                      diagramName: event.target.value,
                      stateValues: [],
                    });
                  }
                }}
                disabled={editing}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                label="Component Diagrams"
              >
                {componentDiagrams.map(diagram => (
                  <MenuItem key={diagram.id} value={diagram.name}>
                    {diagram.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {compDiagram ? (
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Checkbox
                    checked={defaultValues}
                    value={defaultValues}
                    onChange={e => {
                      setDefaultValues(e.target.checked);
                    }}
                  />
                }
                label="Use default diagram values"
              />
            ) : (
              <></>
            )}
            {defaultValues ? (
              <></>
            ) : (
              <StateValuesTable
                componentNode={currentNode}
                setCurrentNodeStateValues={setCurrentNodeStateValues}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5 }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => {
                  handleSave();
                }}
                disabled={hasError}
              >
                Save
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  handleClose();
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <MainDetailsForm
            itemType="LogicNode"
            type={gateTypeValue}
            setType={setGateTypeValue}
            typeOptions={gateTypeOptions}
            name={name}
            handleNameChange={handleNameChange}
            desc={desc}
            setDesc={setDesc}
            nameError={checkForDuplicateNames()}
            error={hasError}
            errorMessage="An node with this name already exists, or includes an invalid character."
            handleSave={() => {
              handleSave();
            }}
            reqPropsFilled={reqPropsFilled}
          >
            <FormControlLabel
              label="Make available as Top or Subtree"
              disabled={fromSidebar === true || availableAsTopOrSubtree()}
              control={
                <Checkbox
                  checked={isRoot}
                  onChange={e => {
                    setIsRoot(e.target.checked);
                  }}
                />
              }
            />
          </MainDetailsForm>
        )}
      </form>
    </Box>
  );
};
